import { Plus } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import type { ContaDaPessoa, ResumoDeConvenio } from "@/api/contrato";
import { useAlterarConta, useBloquearConvenio } from "@/api/consultas";
import { useAvisos } from "@/componentes/Avisos";
import { Botao } from "@/componentes/Botao";
import { Interruptor } from "@/componentes/Interruptor";
import { MenuDeLinha, type ItemDoMenu } from "@/componentes/MenuDeLinha";
import { Picote } from "@/componentes/Picote";
import { Pilula, PilulaDeExcecao, type Matiz } from "@/componentes/PilulaDeSituacao";
import { useAlternarSituacao } from "@/componentes/useAlternarSituacao";
import { banco } from "@/formato";
import { rotaDoConvenio } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { comDigito, rotuloDoTipo } from "./conta";
import { corpoDoDesbloqueio } from "./convenio";
import { textoDaRecusa } from "./recusas";

/*
 * Uma conta da aba, com os convênios dela embaixo: o drill-down Conta → Convênio.
 *
 * Um componente por linha porque `useAlterarConta` e `useBloquearConvenio` têm o
 * identificador no gancho. A conta aparece como uma linha da ficha, rótulo sobre valor:
 * ela tem pouco a mostrar, e quem carrega o detalhe é o convênio (design D1).
 *
 * O interruptor pede a confirmação: desligar exige motivo, e o estado na tela só muda
 * quando a API confirmar e a leitura voltar. O menu ••• guarda só o que não está à vista
 * na linha: repetir ali o interruptor e o `Novo convênio` seria dar dois controles para
 * uma intenção, um ao lado do outro.
 */

const ROTULO_DO_MODO: Readonly<Record<string, string>> = {
  webservice: "WebService",
  misto: "Misto",
  manual: "Manual",
  automatico_van: "Automático (VAN)",
};

const MATIZ_DO_CONVENIO: Readonly<Record<string, Matiz>> = {
  bloqueado: "falha",
};

/** A `Confirmacao` mostra o texto do erro: a recusa chega nela já em vocabulário de tela. */
async function comRecusaTraduzida(pedido: () => Promise<void>) {
  try {
    await pedido();
  } catch (falha) {
    throw new Error(textoDaRecusa(falha), { cause: falha });
  }
}

export function ContaComConvenios({
  pessoaID,
  conta,
  ehAdmin,
  semConvenioNaLista,
  aoEditar,
  aoNovoConvenio,
}: {
  pessoaID: string;
  conta: ContaDaPessoa;
  ehAdmin: boolean;
  /** A lista de pendências já diz `Sem convênio` desta conta, e já oferece a ação. */
  semConvenioNaLista: boolean;
  aoEditar: () => void;
  aoNovoConvenio: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const alterar = useAlterarConta(cliente, pessoaID, conta.conta_id);

  const nome = `${conta.agencia}/${conta.conta}`;
  const ativa = conta.status === "ativa";

  const situacao = useAlternarSituacao({
    ligado: ativa,
    desligar: {
      titulo: `Inativar a conta ${nome}?`,
      corpo: "Recusa emissão nova pelos convênios desta conta; os boletos emitidos não mudam.",
      verbo: "Inativar conta",
    },
    ligar: {
      titulo: `Reativar a conta ${nome}?`,
      corpo: "As emissões pelos convênios desta conta voltam a ser aceitas.",
      verbo: "Reativar conta",
    },
    aoAlterar: (ligar, motivo) =>
      comRecusaTraduzida(async () => {
        await alterar.mutateAsync(ligar ? { status: "ativa" } : { status: "inativa", motivo });
        avisos.feito(ligar ? "Conta reativada" : "Conta inativada");
      }),
  });

  const itens: readonly ItemDoMenu[] = ehAdmin
    ? [{ tipo: "acao", rotulo: "Editar conta", aoEscolher: aoEditar }]
    : [];

  return (
    <li className="rounded-cartao border border-fio bg-superficie">
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {/* `min-h-7` é a altura do interruptor ao lado, com ou sem a pílula. */}
          <div className="flex min-h-7 items-center gap-2">
            <h2 className="text-corpo font-semibold text-texto">{banco(conta.banco)}</h2>
            <PilulaDeExcecao normal={ativa} matiz="encerrado">
              {conta.status}
            </PilulaDeExcecao>
          </div>

          <dl className="flex flex-wrap gap-x-8 gap-y-2">
            <ValorDaConta rotulo="Agência">{comDigito(conta.agencia, conta.agencia_dv)}</ValorDaConta>
            <ValorDaConta rotulo="Conta">{comDigito(conta.conta, conta.conta_dv)}</ValorDaConta>
            <ValorDaConta rotulo="Código do beneficiário">
              {conta.codigo_beneficiario || "—"}
            </ValorDaConta>
            <ValorDaConta rotulo="Tipo" texto>
              {rotuloDoTipo(conta.tipo)}
            </ValorDaConta>
            {conta.banco_correspondente && (
              <ValorDaConta rotulo="Banco correspondente">
                {conta.banco_correspondente}
              </ValorDaConta>
            )}
          </dl>
        </div>

        <div className="flex h-7 items-center gap-2">
          {ehAdmin && (
            <Interruptor
              ligado={ativa}
              rotulo={`Conta ${nome} ativa`}
              aoAlternar={situacao.pedir}
            />
          )}
          <MenuDeLinha rotulo={`Ações da conta ${nome}`} itens={itens} />
        </div>
      </div>

      {/* Com a pendência na tela, a conta sem convênio não repete o fato nem a ação. */}
      {!(conta.convenios.length === 0 && semConvenioNaLista) && (
        <>
          <Picote />

          <section aria-label={`Convênios da conta ${nome}`}>
            <div className="flex min-h-11 items-center justify-between gap-3 px-4 py-1.5">
              {conta.convenios.length === 0 ? (
                <p className="text-texto-2">Nenhum convênio.</p>
              ) : (
                <h3 className="rotulo">Convênios</h3>
              )}
              {ehAdmin && (
                <Botao tom="secundario" onClick={aoNovoConvenio}>
                  <Plus aria-hidden="true" className="size-4" />
                  Novo convênio
                </Botao>
              )}
            </div>

            {conta.convenios.length > 0 && (
              <ul>
                {conta.convenios.map((convenio) => (
                  <LinhaDoConvenio
                    key={convenio.id}
                    pessoaID={pessoaID}
                    convenio={convenio}
                    contaInativa={!ativa}
                    ehAdmin={ehAdmin}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      {situacao.dialogo}
    </li>
  );
}

function ValorDaConta({
  rotulo,
  texto = false,
  children,
}: {
  rotulo: string;
  /** Palavra, e não identificador: fora do monoespaçado. */
  texto?: boolean;
  children: string;
}) {
  return (
    <div className="flex flex-col gap-[2px]">
      <dt className="rotulo">{rotulo}</dt>
      <dd className={texto ? "text-texto" : "dados text-texto"}>{children}</dd>
    </div>
  );
}

/*
 * O link é a linha, menos os controles: um `<a>` por cima de todo o `<li>` engoliria o
 * interruptor e o menu. Assim ctrl+clique abre em aba nova, e o `state` leva junto de
 * que lista o operador veio, para o "« voltar".
 *
 * Com a conta inativa o convênio não emite, seja qual for o `status` dele: a pílula diz
 * isso, e o interruptor apaga. O controle continua respondendo só por `PUT /bloqueio`.
 */
function LinhaDoConvenio({
  pessoaID,
  convenio,
  contaInativa,
  ehAdmin,
}: {
  pessoaID: string;
  convenio: ResumoDeConvenio;
  contaInativa: boolean;
  ehAdmin: boolean;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const estado: unknown = useLocation().state;
  const bloquear = useBloquearConvenio(cliente, convenio.id);

  // `provisionando` e `pendente_credencial` não são bloqueio, e religar grava `ativo`:
  // o interruptor só responde pela pergunta que `PUT /bloqueio` sabe responder.
  const liberado = convenio.status !== "bloqueado";

  const situacao = useAlternarSituacao({
    ligado: liberado,
    desligar: {
      titulo: `Bloquear o convênio ${convenio.numero}?`,
      corpo: "Recusa emissão nova por este convênio; os boletos emitidos não mudam.",
      verbo: "Bloquear convênio",
    },
    ligar: {
      titulo: `Desbloquear o convênio ${convenio.numero}?`,
      corpo: corpoDoDesbloqueio(contaInativa),
      verbo: "Desbloquear convênio",
    },
    aoAlterar: (ligar, motivo) =>
      comRecusaTraduzida(async () => {
        await bloquear.mutateAsync(ligar ? { bloqueado: false } : { bloqueado: true, motivo });
        avisos.feito(ligar ? "Convênio desbloqueado" : "Convênio bloqueado");
      }),
  });

  const itens: readonly ItemDoMenu[] = ehAdmin
    ? [
        {
          tipo: "link",
          rotulo: "Editar convênio",
          para: rotaDoConvenio(pessoaID, convenio.id, { abrir: "editar" }),
        },
      ]
    : [];

  return (
    <li className="flex min-h-10 items-center gap-3 border-t border-fio px-4 last:rounded-b-cartao hover:bg-superficie-2">
      <Link
        to={rotaDoConvenio(pessoaID, convenio.id)}
        state={estado}
        className="flex min-w-0 flex-1 items-center gap-4 self-stretch"
      >
        <span className="dados text-texto">{convenio.numero}</span>
        <span className="whitespace-nowrap text-texto-2">
          Carteira{" "}
          <span className="dados">
            {convenio.carteira}
            {convenio.variacao ? `/${convenio.variacao}` : ""}
          </span>
        </span>
        {convenio.descricao && (
          <span className="min-w-0 truncate text-texto-2">{convenio.descricao}</span>
        )}
        <span className="ml-auto whitespace-nowrap text-texto-2">
          {ROTULO_DO_MODO[convenio.modo_registro] ?? convenio.modo_registro}
        </span>
      </Link>

      {(!contaInativa || convenio.status !== "ativo") && (
        <PilulaDeExcecao
          normal={convenio.status === "ativo"}
          matiz={MATIZ_DO_CONVENIO[convenio.status] ?? "transito"}
        >
          {convenio.status.replaceAll("_", " ")}
        </PilulaDeExcecao>
      )}
      {contaInativa && <Pilula matiz="encerrado">conta inativa</Pilula>}

      {ehAdmin && (
        <Interruptor
          ligado={liberado}
          apagado={contaInativa}
          rotulo={
            contaInativa
              ? `Convênio ${convenio.numero} sem bloqueio, conta inativa`
              : `Convênio ${convenio.numero} ativo`
          }
          aoAlternar={situacao.pedir}
        />
      )}
      <MenuDeLinha rotulo={`Ações do convênio ${convenio.numero}`} itens={itens} />

      {situacao.dialogo}
    </li>
  );
}
